import {
  InfoOutlined,
  NotificationsActive,
  NotificationsOff,
  VolumeUp,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Slider,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'common/context/hooks';
import { orderActions } from 'context/OrderSlice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSoundPlayer } from '../hooks/useSoundPlayer';

const notificationFeatures = [
  {
    icon: <NotificationsActive sx={{ fontSize: 24, color: 'white' }} />,
    title: 'Gerçek Zamanlı Bildirimler',
    description:
      'Yeni siparişler geldiğinde anında sesli uyarı alırsınız. Bu sayede hiçbir siparişi kaçırmazsınız.',
  },
  {
    icon: <VolumeUp sx={{ fontSize: 24, color: 'white' }} />,
    title: 'Ses Testi',
    description:
      'Ses seviyesini ve bildirim sesini test edebilirsiniz. Böylece bildirimlerin çalıştığından emin olursunuz.',
    clickable: true,
  },
  {
    icon: <NotificationsOff sx={{ fontSize: 24, color: 'white' }} />,
    title: 'Kolay Kontrol',
    description:
      'Tek tıkla sesli bildirimleri açıp kapatabilirsiniz. İhtiyacınıza göre ayarlayabilirsiniz.',
  },
];

export const NotificationManager = () => {
  const { onPlaySound, SOUND_URL } = useSoundPlayer();
  const dispatch = useAppDispatch();
  const { newOrderCount, isSoundEnabled: soundEnabled } = useAppSelector(
    (state) => state.order,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [volume, setVolume] = useState(70);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const onSoundToggle = useCallback(
    (enabled: boolean) => dispatch(orderActions.setIsSoundEnabled(enabled)),
    [dispatch],
  );

  // Ses dosyasını önceden yükle
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.src = SOUND_URL;
      audioRef.current.load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSoundToggle = useCallback(() => {
    const newSoundState = !soundEnabled;
    onSoundToggle(newSoundState);
    // Ses açıldığında test sesi çal
    if (newSoundState) {
      onPlaySound(volume);
    }
  }, [soundEnabled, onSoundToggle, onPlaySound, volume]);

  const handleInfoClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleTestSound = () => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
      audioRef.current.play().catch(() => {
        // Ses çalma hatası durumunda sessizce devam et
      });
    }
  };

  const handleVolumeChange = (event: Event, newValue: number | number[]) => {
    const newVolume = newValue as number;
    setVolume(newVolume);
  };

  useEffect(() => {
    console.log('newOrderCount', newOrderCount);
    if (newOrderCount > 0) {
      onPlaySound(volume);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOrderCount, onPlaySound]);

  return (
    <>
      <Grid container>
        <Grid
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 4,
            p: [0.5, 0],
            width: '90px',
          }}
          wrap="nowrap"
          justifyContent="space-between"
          alignItems="center"
          container
        >
          <Grid item>
            <Badge badgeContent={newOrderCount} color="error">
              <Tooltip
                title={
                  soundEnabled
                    ? 'Sesli alarm açık - Yeni sipariş geldiğinde ses çalacak'
                    : 'Sesli alarm kapalı - Yeni sipariş geldiğinde ses çalmayacak'
                }
              >
                <IconButton
                  onClick={handleSoundToggle}
                  color={soundEnabled ? 'primary' : 'default'}
                  size="small"
                  sx={{
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  {soundEnabled ? (
                    <NotificationsActive />
                  ) : (
                    <NotificationsOff />
                  )}
                </IconButton>
              </Tooltip>
            </Badge>
          </Grid>
          <Grid item>
            <Tooltip title="Bildirim ayarları hakkında bilgi" placement="top">
              <IconButton onClick={handleInfoClick} size="small">
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>

      {/* Notification Info Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: 400,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              🔔 Sesli Bildirim Sistemi
            </Typography>
            <Chip
              label="AKTİF"
              size="small"
              color="success"
              sx={{
                fontWeight: 'bold',
                backgroundColor: '#4caf50',
                color: 'white',
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Yeni siparişler geldiğinde sesli uyarı alarak hiçbir siparişi
            kaçırmayın
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
              🎯 Nasıl Çalışır?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sistem her 30 saniyede bir yeni sipariş kontrolü yapar. Eğer yeni
              sipariş varsa ve sesli bildirimler açıksa, otomatik olarak sesli
              uyarı çalar. Bu sayede siparişlerinizi anında fark edebilirsiniz.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
              ✨ Özellikler
            </Typography>
            <Grid container spacing={2}>
              {notificationFeatures.map((feature, index) => (
                <Grid item xs={12} key={index}>
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 3,
                      backgroundColor: 'background.paper',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      transition: 'all 0.2s ease-in-out',
                      cursor: feature.clickable ? 'pointer' : 'default',
                      '&:hover': {
                        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                        transform: feature.clickable
                          ? 'translateY(-2px)'
                          : 'none',
                        backgroundColor: feature.clickable
                          ? 'rgba(102, 126, 234, 0.04)'
                          : 'background.paper',
                      },
                    }}
                    onClick={feature.clickable ? handleTestSound : undefined}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '12px',
                            background:
                              'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            position: 'relative',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              borderRadius: '12px',
                              background:
                                'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                            },
                          }}
                        >
                          <Box sx={{ position: 'relative', zIndex: 1 }}>
                            {feature.icon}
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs>
                        <Typography
                          variant="h6"
                          fontWeight="600"
                          sx={{ mb: 1 }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.description}
                        </Typography>
                        {feature.clickable && (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{
                              mt: 1,
                              display: 'block',
                              fontStyle: 'italic',
                              fontWeight: 500,
                            }}
                          >
                            💡 Tıklayarak ses testi yapın
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Ses Seviyesi Ayarı */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
              🔊 Ses Seviyesi Ayarı
            </Typography>
            <Box
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                backgroundColor: 'background.paper',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <VolumeUp sx={{ fontSize: 24, color: 'primary.main' }} />
                </Grid>
                <Grid item xs>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Bildirim sesinin seviyesini ayarlayın. Şu anki seviye:{' '}
                    <strong>{volume}%</strong>
                  </Typography>
                  <Slider
                    value={volume}
                    onChange={handleVolumeChange}
                    aria-labelledby="volume-slider"
                    min={10}
                    max={100}
                    step={5}
                    marks={[
                      { value: 10, label: '10%' },
                      { value: 50, label: '50%' },
                      { value: 100, label: '100%' },
                    ]}
                    sx={{
                      '& .MuiSlider-mark': {
                        backgroundColor: 'primary.main',
                      },
                      '& .MuiSlider-markLabel': {
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                      },
                    }}
                  />
                </Grid>
                <Grid item>
                  <Button
                    onClick={handleTestSound}
                    variant="outlined"
                    color="primary"
                    startIcon={<VolumeUp />}
                    size="small"
                  >
                    Test Et
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box
            sx={{
              p: 2.5,
              backgroundColor: 'rgba(76, 175, 80, 0.04)',
              borderRadius: 2,
              mb: 2,
              border: '1px solid rgba(76, 175, 80, 0.12)',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>💡 İpucu:</strong> Sesli bildirimler açıkken yeni sipariş
              geldiğinde otomatik olarak ses çalar. Eğer ses duymuyorsanız,
              tarayıcınızın ses ayarlarını kontrol edin ve bu sayfaya ses izni
              verdiğinizden emin olun.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseModal}
            variant="contained"
            color="primary"
          >
            Anladım
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ses dosyası için audio element */}
      <audio
        ref={audioRef}
        preload="auto"
        onEnded={() => {
          // Ses bittiğinde temizlik yap
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
          }
        }}
        onError={(e) => {
          // Audio element hatası durumunda sessizce devam et
        }}
      />
    </>
  );
};
